import { useState, useEffect, useMemo } from 'react';
import { safeGetJSON, safeSetJSON } from '@/lib/safeStorage';
import { rooms } from '@/data/rooms';

interface DailyPromptData {
  prompt: string;
  roomSuggestion: string;
  date: string;
}

const STORAGE_KEY = 'human-rooms-daily-prompt';

const PROMPTS = [
  { text: "What truth have you been circling around?", rooms: ['one-honest-sentence', 'sentence-you-avoid'] },
  { text: "When did you last surprise yourself?", rooms: ['default-choice', 'shortcut-always-take'] },
  { text: "What would change if you stopped pretending?", rooms: ['if-nothing-changes', 'blurred-self'] },
  { text: "Which version of you is reading this?", rooms: ['parallel-you', 'your-shadow'] },
  { text: "What keeps returning, uninvited?", rooms: ['thought-that-returns', 'thought-loop'] },
  { text: "Where do you go when no one's watching?", rooms: ['room-you-avoid', 'room-you-visit-most'] },
  { text: "What would you say to yourself at 3AM?", rooms: ['you-at-317am', 'what-keeps-awake'] },
  { text: "How long since you've been still?", rooms: ['nothing-happens', 'waiting-game'] },
  { text: "What are you really waiting for?", rooms: ['delay-button', 'this-moment'] },
  { text: "What question would change everything?", rooms: ['question-never-answer', 'last-conversation'] },
  { text: "When did you stop listening to yourself?", rooms: ['listen-to-yourself', 'voice-without-meaning'] },
  { text: "What shape does your life take today?", rooms: ['life-as-shapes', 'day-in-pixels'] },
  { text: "What's the line you won't cross again?", rooms: ['line-you-crossed', 'decision-shadow'] },
  { text: "Who are you without your roles?", rooms: ['who-without-job', 'face-without-features'] },
  { text: "What did today cost you?", rooms: ['reality-receipt', 'real-priorities'] },
];

const getDailyPrompt = (): DailyPromptData => {
  const today = new Date().toISOString().split('T')[0];
  const stored = safeGetJSON<DailyPromptData | null>(STORAGE_KEY, null);
  
  if (stored && stored.date === today) {
    return stored;
  }
  
  // Use date as seed for consistent daily prompt
  const dayOfYear = Math.floor(
    (Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / (1000 * 60 * 60 * 24)
  );
  const promptIndex = dayOfYear % PROMPTS.length;
  const selectedPrompt = PROMPTS[promptIndex];
  const roomIndex = dayOfYear % selectedPrompt.rooms.length;
  
  const newPrompt: DailyPromptData = {
    prompt: selectedPrompt.text,
    roomSuggestion: selectedPrompt.rooms[roomIndex],
    date: today,
  };
  
  safeSetJSON(STORAGE_KEY, newPrompt);
  return newPrompt;
};

export const useDailyPrompt = () => {
  const [data, setData] = useState<DailyPromptData>(() => getDailyPrompt());
  
  useEffect(() => {
    setData(getDailyPrompt());
  }, []);

  const suggestedRoom = useMemo(() => {
    return rooms.find(r => r.id === data.roomSuggestion);
  }, [data.roomSuggestion]);

  return {
    prompt: data.prompt,
    suggestedRoom,
    date: data.date,
  };
};
